/**
 * Get Started widget state store.
 *
 * The widget has two states the user toggles between, both bottom-right:
 *
 *  - `expanded`  — full panel with the step list (default)
 *  - `collapsed` — a checklist-icon circle above the OptiBot FAB
 *
 * There used to be a third, `minimized`, which hid the panel entirely and put a
 * restore pill in the top navigation. That was removed along with the pill: it
 * took the guide out of the workspace, and it also meant the corner could be
 * free while the checklist was still "open", which let the referral launcher
 * appear alongside an unfinished checklist. Now the widget is either present
 * (expanded or collapsed) or dismissed for good.
 *
 * Persisted to localStorage so the chosen state survives reloads — collapsing
 * the widget once shouldn't make it pop back open on every page navigation.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type GetStartedWidgetState = 'expanded' | 'collapsed';

interface GetStartedWidgetStore {
  /** Current widget state — see file header. */
  widgetState: GetStartedWidgetState;
  /** Set state directly. */
  setWidgetState: (state: GetStartedWidgetState) => void;
  /**
   * Step the panel should open next, requested from OUTSIDE the panel.
   * `GetStartedChecklist` owns `activeStepId` in local state, so this is a
   * one-shot REQUEST it consumes and clears — not a second source of truth for
   * which step is open. Deliberately absent from `partialize`: an accordion
   * position is not a preference to persist.
   */
  pendingStepId: string | null;
  /** Expand the panel and queue `stepId` to be opened. */
  openStep: (stepId: string) => void;
  /** Called by the panel once it has applied `pendingStepId`. */
  clearPendingStep: () => void;
  /**
   * True once the user clicks the download link in the "Get your screen set
   * up" checklist step. That step has no backend signal — it completes purely
   * on this client-side action. Persisted so the check survives reloads.
   */
  screenSetupDownloadClicked: boolean;
  /** Marks the download link as clicked — completes the "Get your screen set up" step. */
  markScreenSetupDownloadClicked: () => void;
}

export const useGetStartedWidgetStore = create<GetStartedWidgetStore>()(
  persist(
    (set) => ({
      widgetState: 'expanded',
      setWidgetState: (widgetState) => set({ widgetState }),
      pendingStepId: null,
      openStep: (stepId) =>
        set({
          widgetState: 'expanded',
          pendingStepId: stepId,
        }),
      clearPendingStep: () => set({ pendingStepId: null }),
      screenSetupDownloadClicked: false,
      markScreenSetupDownloadClicked: () => set({ screenSetupDownloadClicked: true }),
    }),
    {
      name: 'scio3-get-started-widget',
      partialize: (s) => ({
        widgetState: s.widgetState,
        screenSetupDownloadClicked: s.screenSetupDownloadClicked,
      }),
      // v0 -> v1: `minimized` no longer exists. Anyone who left the widget
      // minimized has that string in localStorage; map it to `expanded` so they
      // don't hydrate into a value no branch handles (which would render as
      // expanded anyway, but implicitly — better to normalise the stored data).
      version: 1,
      migrate: (persisted) => {
        const s = (persisted ?? {}) as Partial<{
          widgetState: string;
          screenSetupDownloadClicked: boolean;
        }>;
        return {
          ...s,
          widgetState: s.widgetState === 'collapsed' ? 'collapsed' : 'expanded',
        } as { widgetState: GetStartedWidgetState; screenSetupDownloadClicked: boolean };
      },
    },
  ),
);
